import { ColumnDef } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getOuraOperationsOptions } from "@/services/queryOptions/ouraQueryOptions";
import type { OuraListParams, OuraOperation } from "@/types/oura";
import { AdminDataTable } from "../components/AdminDataTable";

function short(value?: string | null) {
  if (!value) return "-";
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

export function OperationsManager({
  resourceType,
  compact = false,
}: {
  resourceType?: string;
  compact?: boolean;
}) {
  const [params, setParams] = useState<OuraListParams>({
    page: 1,
    page_size: compact ? 10 : 25,
    resource_type: resourceType,
  });
  const query = useQuery(getOuraOperationsOptions(params));

  const columns = useMemo<ColumnDef<OuraOperation>[]>(
    () => [
      {
        accessorKey: "createdAt",
        header: "Time",
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleString(),
      },
      {
        accessorKey: "resourceType",
        header: "Resource",
        cell: ({ row }) => <Badge variant="outline">{row.original.resourceType}</Badge>,
      },
      { accessorKey: "actionType", header: "Action" },
      {
        accessorKey: "signature",
        header: "Signature",
        cell: ({ row }) =>
          row.original.signature ? (
            <a
              className="font-mono text-cyan-300 hover:underline"
              href={`https://solscan.io/tx/${row.original.signature}`}
              target="_blank"
              rel="noreferrer"
            >
              {short(row.original.signature)}
            </a>
          ) : (
            "-"
          ),
      },
      {
        accessorKey: "collection",
        header: "Collection",
        cell: ({ row }) => <span className="font-mono">{short(row.original.collection)}</span>,
      },
      {
        accessorKey: "asset",
        header: "Asset",
        cell: ({ row }) => <span className="font-mono">{short(row.original.asset)}</span>,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <Badge>{row.original.status}</Badge>,
      },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{compact ? "Recent Transactions" : "Oura History"}</CardTitle>
      </CardHeader>
      <CardContent>
        <AdminDataTable
          columns={columns}
          data={query.data?.items ?? []}
          total={query.data?.total ?? 0}
          params={params}
          onParamsChange={(next) => setParams({ ...next, resource_type: resourceType })}
          exportName="oura-history.csv"
        />
      </CardContent>
    </Card>
  );
}
